package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.SyncReportDto
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File
import org.w3c.files.get

@Composable
fun ReaderSyncForm(
    apiClient: ApiClient,
    onSyncComplete: (SyncReportDto) -> Unit = {},
    onCancel: (() -> Unit)? = null
) {
    val colors = AppTheme.colors
    var selectedFile by remember { mutableStateOf<File?>(null) }
    var isSyncing by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var syncReport by remember { mutableStateOf<SyncReportDto?>(null) }

    val scope = rememberCoroutineScope()

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            themeTransition()
        }
    }) {
        // Title
        H2({
            style {
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.lg)
            }
        }) {
            Text("Sync Readers from CSV")
        }

        // Description
        P({
            style {
                fontSize(Typography.base)
                color(Color(colors.textMuted))
                marginBottom(Spacing.xxl)
            }
        }) {
            Text("Upload the same CSV file to extract reader information and link them to existing audiobooks. This will only update reader associations without re-importing audiobooks.")
        }

        // File selection or report display
        if (syncReport == null) {
            SyncFormField("CSV File") {
                Input(InputType.File) {
                    attr("accept", ".csv")
                    if (isSyncing) {
                        disabled()
                    }
                    onChange { event ->
                        val files = event.target.files
                        if (files != null && files.length > 0) {
                            selectedFile = files[0]
                            errorMessage = null
                        }
                    }
                }
            }

            // Selected file info
            selectedFile?.let { file ->
                Div({
                    style {
                        backgroundColor(Color(colors.surfaceHover))
                        padding(Spacing.md)
                        borderRadius(BorderRadius.md)
                        marginTop(Spacing.md)
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.text))
                            fontWeight("500")
                        }
                    }) {
                        Text("Selected file: ${file.name}")
                    }
                    Div({
                        style {
                            fontSize(13.px)
                            color(Color(colors.textMuted))
                            marginTop(Spacing.xs)
                        }
                    }) {
                        val sizeKb = (file.size.toDouble() / 1024.0).toInt()
                        Text("Size: ${sizeKb} KB")
                    }
                }
            }

            // Progress indicator
            if (isSyncing) {
                Div({
                    style {
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.text))
                            marginBottom(Spacing.sm)
                            fontWeight("500")
                        }
                    }) {
                        Text("Syncing readers...")
                    }

                    // Progress bar container
                    Div({
                        style {
                            width(100.percent)
                            height(8.px)
                            backgroundColor(Color(colors.border))
                            borderRadius(BorderRadius.sm)
                            overflow("hidden")
                        }
                    }) {
                        // Progress bar fill (indeterminate animation)
                        Div({
                            style {
                                height(100.percent)
                                width(100.percent)
                                backgroundColor(Color(colors.success))
                                property("animation", "indeterminate 1.5s ease-in-out infinite")
                            }
                        })
                    }
                }
            }

            // Error message
            errorMessage?.let { error ->
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        border(1.px, LineStyle.Solid, Color(colors.danger))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.md)
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                            fontWeight("500")
                        }
                    }) {
                        Text("Error")
                    }
                    Div({
                        style {
                            fontSize(13.px)
                            color(Color(colors.danger))
                            marginTop(Spacing.xs)
                        }
                    }) {
                        Text(error)
                    }
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(Spacing.md)
                    marginTop(Spacing.xxl)
                }
            }) {
                if (onCancel != null) {
                    Button({
                        if (isSyncing) {
                            disabled()
                        }
                        onClick {
                            if (!isSyncing) {
                                onCancel()
                            }
                        }
                        style {
                            padding(10.px, Spacing.xl)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color(colors.text))
                            backgroundColor(Color(colors.surface))
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            cursor(if (isSyncing) "not-allowed" else "pointer")
                        }
                    }) {
                        Text("Cancel")
                    }
                }

                Button({
                    if (selectedFile == null || isSyncing) {
                        disabled()
                    }
                    onClick {
                        val file = selectedFile
                        if (file != null && !isSyncing) {
                            scope.launch {
                                isSyncing = true
                                errorMessage = null
                                try {
                                    val report = apiClient.syncReadersFromCsv(file)
                                    syncReport = report
                                    onSyncComplete(report)
                                } catch (e: Exception) {
                                    errorMessage = e.message ?: "Sync failed"
                                } finally {
                                    isSyncing = false
                                }
                            }
                        }
                    }
                    style {
                        padding(10.px, Spacing.xl)
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color("#ffffff"))
                        backgroundColor(
                            if (selectedFile == null || isSyncing) Color(colors.textMuted)
                            else Color(colors.success)
                        )
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor(if (selectedFile == null || isSyncing) "not-allowed" else "pointer")
                    }
                }) {
                    Text(if (isSyncing) "Syncing..." else "Sync Readers")
                }
            }
        } else {
            // Display sync report
            val report = syncReport!!

            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#f0fdf4" else "#052e16"))
                    border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#86efac" else "#166534"))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.lg)
                    marginBottom(Spacing.lg)
                }
            }) {
                H3({
                    style {
                        fontSize(Typography.lg)
                        fontWeight("600")
                        color(Color(if (colors == LightColors) "#166534" else "#86efac"))
                        marginBottom(Spacing.md)
                    }
                }) {
                    Text("Sync Complete")
                }

                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(if (colors == LightColors) "#15803d" else "#86efac"))
                        marginBottom(Spacing.sm)
                    }
                }) {
                    Text("Scanned ${report.scannedCount} audiobooks")
                }

                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(if (colors == LightColors) "#15803d" else "#86efac"))
                        marginBottom(Spacing.sm)
                    }
                }) {
                    Text("Synced ${report.syncedCount} readers")
                }

                if (report.skippedCount > 0) {
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.warning))
                            marginBottom(Spacing.sm)
                        }
                    }) {
                        Text("⊘ Skipped ${report.skippedCount} (no reader or already linked)")
                    }
                }

                if (report.errors.isNotEmpty()) {
                    Div({
                        style {
                            marginTop(Spacing.md)
                            padding(Spacing.md)
                            backgroundColor(Color(if (colors == LightColors) "#fef3c7" else "#451a03"))
                            borderRadius(BorderRadius.sm)
                        }
                    }) {
                        Div({
                            style {
                                fontSize(Typography.base)
                                fontWeight("600")
                                color(Color(if (colors == LightColors) "#92400e" else "#fcd34d"))
                                marginBottom(Spacing.sm)
                            }
                        }) {
                            Text("${report.errors.size} Errors:")
                        }
                        report.errors.take(5).forEach { error ->
                            Div({
                                style {
                                    fontSize(13.px)
                                    color(Color(if (colors == LightColors) "#78350f" else "#fcd34d"))
                                    marginBottom(Spacing.xs)
                                }
                            }) {
                                Text("• $error")
                            }
                        }
                        if (report.errors.size > 5) {
                            Div({
                                style {
                                    fontSize(13.px)
                                    color(Color(if (colors == LightColors) "#78350f" else "#fcd34d"))
                                    marginTop(Spacing.sm)
                                    fontStyle("italic")
                                }
                            }) {
                                Text("...and ${report.errors.size - 5} more errors")
                            }
                        }
                    }
                }
            }

            // Close button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    marginTop(Spacing.lg)
                }
            }) {
                Button({
                    onClick {
                        onCancel?.invoke()
                    }
                    style {
                        padding(10.px, Spacing.xl)
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color("#ffffff"))
                        backgroundColor(Color(colors.success))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                    }
                }) {
                    Text("Done")
                }
            }
        }
    }
}

@Composable
private fun SyncFormField(label: String, content: @Composable () -> Unit) {
    val colors = AppTheme.colors
    Div({
        style {
            marginBottom(Spacing.lg)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Block)
                fontSize(Typography.base)
                fontWeight("500")
                color(Color(colors.text))
                marginBottom(BorderRadius.md)
            }
        }) {
            Text(label)
        }
        content()
    }
}
